import { ProcessedAIData, AIOverviewData } from '../types';
import { 
  arrayToCSV, 
  downloadCSV, 
  downloadPDF, 
  createPDF, 
  addTableToPDF,
  addPageBreakIfNeeded,
  formatTimeForDisplay,
  generateFilename,
  getDataModeLabel,
  getPeriodLabel
} from './exportUtils';
import { formatAITime } from './aiOverviewService';

/**
 * Export service for AI Overview board (AIOverviewTable component)
 */

export interface AIOverviewExportOptions {
  dataMode: string;
  reportingPeriod: string;
  selectedWeek: string;
}

/**
 * Prepare AI Overview data for CSV export
 */
const prepareAIOverviewDataForCSV = (aiData: ProcessedAIData): any[] => {
  return aiData.users.map((user, index) => ({
    Rank: index + 1,
    'Team Member': user.teamMemberName,
    'Total AI Time': formatAITime(user.totalCombinedAITime),
    'Dev AI Time': formatAITime(user.totalAITime),
    'Pure AI Time': formatAITime(user.totalPureAITime),
    'Application Count': user.aiApplications.length,
    'AI Applications': user.aiApplications
      .map(app => `${app.applicationName} (${formatAITime(app.spentTime)})`)
      .join('; ')
  }));
};

/**
 * Prepare top applications data for CSV export
 */
const prepareTopApplicationsForCSV = (aiData: ProcessedAIData): any[] => {
  return aiData.mostUsedAIApps.map((app, index) => ({
    Rank: index + 1,
    'Application Name': app.applicationName,
    'Total Time': formatAITime(app.totalTime),
    'User Count': app.userCount,
    'Average Time per User': formatAITime(app.totalTime / app.userCount)
  }));
};

/**
 * Export AI Overview data to CSV (includes user data and top applications)
 */
export const exportAIOverviewToCSV = (
  aiData: ProcessedAIData,
  options: AIOverviewExportOptions
): void => {
  if (!aiData || aiData.users.length === 0) {
    alert('No AI Overview data available to export');
    return;
  }

  try {
    // Prepare user data
    const userData = prepareAIOverviewDataForCSV(aiData);
    const userCSV = arrayToCSV(userData);
    
    // Prepare top applications data
    const topAppsData = prepareTopApplicationsForCSV(aiData);
    const topAppsCSV = arrayToCSV(topAppsData);
    
    // Combine both datasets with headers
    const combinedCSV = [
      '# AI Overview - Team Member Usage',
      userCSV,
      '',
      '# Most Used AI Applications',
      topAppsCSV
    ].join('\n');
    
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    
    const filename = generateFilename(
      `ai_overview_${dataMode}_${period}_${options.selectedWeek}`,
      'csv'
    );
    
    downloadCSV(combinedCSV, filename);
    console.log('✅ [EXPORT] AI Overview CSV exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export AI Overview CSV:', error);
    alert('Failed to export CSV. Please try again.');
  }
};

/**
 * Export AI Overview data to PDF
 */
export const exportAIOverviewToPDF = (
  aiData: ProcessedAIData,
  options: AIOverviewExportOptions
): void => {
  if (!aiData || aiData.users.length === 0) {
    alert('No AI Overview data available to export');
    return;
  }

  try {
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    
    const title = `AI Usage Overview - ${dataMode} (${period})`;
    const pdf = createPDF(title, 'landscape');

    // Add summary statistics
    let currentY = 40;
    pdf.setFontSize(12);
    pdf.text(`Week: ${options.selectedWeek}`, 20, currentY);
    currentY += 10;
    
    pdf.setFontSize(10);
    pdf.text(`Total Users: ${aiData.totalUsers}`, 20, currentY);
    currentY += 8;
    pdf.text(`Total AI Time: ${formatAITime(aiData.overallAITime + aiData.overallPureAITime)}`, 20, currentY);
    currentY += 8;
    pdf.text(`Dev AI Time: ${formatAITime(aiData.overallAITime)}`, 20, currentY);
    currentY += 8;
    pdf.text(`Pure AI Time: ${formatAITime(aiData.overallPureAITime)}`, 20, currentY);
    currentY += 15;

    // Team Member Usage Table
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Team Member AI Usage', 20, currentY);
    currentY += 10;

    const userHeaders = [
      'Rank',
      'Team Member', 
      'Total AI Time',
      'Dev AI Time',
      'Pure AI Time',
      'Apps'
    ];

    const userTableData = aiData.users.map((user, index) => [
      String(index + 1),
      user.teamMemberName,
      formatAITime(user.totalCombinedAITime),
      formatAITime(user.totalAITime),
      formatAITime(user.totalPureAITime),
      String(user.aiApplications.length)
    ]);

    currentY = addTableToPDF(pdf, userHeaders, userTableData, currentY, {
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 15 }, // Rank
        1: { cellWidth: 40 }, // Team Member
        2: { cellWidth: 25 }, // Total AI Time
        3: { cellWidth: 25 }, // Dev AI Time
        4: { cellWidth: 25 }, // Pure AI Time
        5: { cellWidth: 15 }  // Apps
      }
    }) + 20;

    // Most Used Applications Table
    if (aiData.mostUsedAIApps.length > 0) {
      currentY = addPageBreakIfNeeded(pdf, currentY, 60);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Most Used AI Applications', 20, currentY);
      currentY += 10;

      const appHeaders = [
        'Rank',
        'Application Name',
        'Total Time',
        'Users',
        'Avg Time/User'
      ];

      const appTableData = aiData.mostUsedAIApps.slice(0, 15).map((app, index) => [
        String(index + 1),
        app.applicationName,
        formatAITime(app.totalTime),
        String(app.userCount),
        formatAITime(app.totalTime / app.userCount)
      ]);

      addTableToPDF(pdf, appHeaders, appTableData, currentY, {
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 15 }, // Rank
          1: { cellWidth: 50 }, // Application Name
          2: { cellWidth: 25 }, // Total Time
          3: { cellWidth: 20 }, // Users
          4: { cellWidth: 25 }  // Avg Time/User
        }
      });
    }

    const filename = generateFilename(
      `ai_overview_${dataMode}_${period}_${options.selectedWeek}`,
      'pdf'
    );
    
    downloadPDF(pdf, filename);
    console.log('✅ [EXPORT] AI Overview PDF exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export AI Overview PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};
